import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff, ArrowDownToLine, CreditCard, TrendingUp, RefreshCw, ChevronRight, Wallet, X, AlertCircle, CheckCircle2, Clock } from 'lucide-react';
import { useHistory, Link } from 'react-router-dom';
import MenuClient from '../Components/menuclient';

const API = 'https://appapi.unpaidfinancial.com/api';

const fmtMoney = (n, dev = 'EUR') =>
  `${Number(n ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${dev}`;

const fullName = (u) =>
  (`${u?.prenom ?? ''} ${u?.nom ?? ''}`.trim()) || u?.name || 'Utilisateur';

const Portefeuille = () => {
  const history = useHistory();

  const session = useMemo(() => {
    try {
      const raw = localStorage.getItem('bank.session');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const token = useMemo(() => {
    try {
      return localStorage.getItem('bank.token') || null;
    } catch {
      return null;
    }
  }, []);

  const [showBalance, setShowBalance] = useState(true);
  const [tx, setTx] = useState([]);
  const [loadingTx, setLoadingTx] = useState(true);
  const [selectedTx, setSelectedTx] = useState(null);
  const [refundReason, setRefundReason] = useState('');
  const [isRequestingRefund, setIsRequestingRefund] = useState(false);

  useEffect(() => {
    if (!session || !session.user || !session.account) {
      history.replace('/banque/login');
    }
  }, [session, history]);

  useEffect(() => {
    let cancelled = false;

    async function fetchTx() {
      if (!session || !session.user || !session.account) {
        setTx([]);
        setLoadingTx(false);
        return;
      }
      setLoadingTx(true);
      try {
        const res = await fetch(`${API}/bank/me/transactions?limit=20`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        const payload = await res.json().catch(() => null);
        if (!res.ok || !payload) {
          if (!cancelled) setTx([]);
          return;
        }
        const items = Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload)
          ? payload
          : [];
        if (!cancelled) setTx(items);
      } catch {
        if (!cancelled) setTx([]);
      } finally {
        if (!cancelled) setLoadingTx(false);
      }
    }

    fetchTx();
    return () => { cancelled = true; };
  }, [session, token]);

  const getStatusInfo = (status) => {
    switch (status) {
      case 'completed':
      case 'valide':
        return { label: 'Complété', color: '#16A34A', icon: CheckCircle2 };
      case 'en_attente':
        return { label: 'En attente', color: '#F59E0B', icon: Clock };
      case 'rejected':
        return { label: 'Rejeté', color: '#DC2626', icon: AlertCircle };
      case 'remboursement':
        return { label: 'Remboursement', color: '#8B5CF6', icon: RefreshCw };
      default:
        return { label: 'En cours', color: '#6B7280', icon: Clock };
    }
  };

  const handleRequestRefund = async () => {
    if (!refundReason.trim() || refundReason.trim().length < 10) {
      alert('Veuillez fournir une raison d\'au moins 10 caractères');
      return;
    }

    setIsRequestingRefund(true);
    try {
      const res = await fetch(`${API}/bank/request-refund`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          transaction_id: selectedTx.id,
          reason: refundReason,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        alert(data.error || 'Erreur lors de la demande de remboursement');
        return;
      }

      alert('Demande de remboursement enregistrée avec succès');
      setSelectedTx(null);
      setRefundReason('');
      window.location.reload();
    } catch (error) {
      alert('Erreur réseau. Veuillez réessayer.');
    } finally {
      setIsRequestingRefund(false);
    }
  };

  if (!session || !session.user || !session.account) return null;

  const { user, account, card } = session;
  const devise = account?.devise || 'EUR';
  const beneficiaryName = card?.name_on_card || fullName(user);

  const myAccountId = session.account.id;
  const txItems = (tx || []).map((t) => {
    const incoming =
      (t.to_account_id && myAccountId && t.to_account_id === myAccountId) ||
      t.direction === 'in';
    
    let label = 'Opération';
    if (t.type === 'admin' && incoming) label = 'Rechargement';
    else if (incoming) label = 'Virement reçu';
    else label = 'Virement envoyé';
    if (t.description) label = t.description;

    return {
      id: t.id || `${t.created_at}-${t.amount}-${Math.random()}`,
      label,
      amount: Number(t.amount ?? 0),
      incoming,
      date: t.created_at ? new Date(t.created_at) : null,
      status: t.status,
      recipient: t.recipient,
      type: t.type,
      rawData: t,
    };
  });

  return (
    <div style={styles.page}>
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* Hero Section avec gradient */}
      <motion.div 
        style={styles.heroSection}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <div style={styles.heroContent}>
          <motion.div 
            style={styles.headerTop}
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <div style={styles.logoContainer}>
              <Wallet size={28} strokeWidth={2.5} />
              <span style={styles.bankName}>AidFoundation</span>
            </div>
            <button
              onClick={() => setShowBalance((v) => !v)}
              style={styles.eyeButton}
              title={showBalance ? 'Masquer le solde' : 'Afficher le solde'}
            >
              {showBalance ? <Eye size={20} /> : <EyeOff size={20} />}
            </button>
          </motion.div>

          <motion.div
            initial={{ y: -10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            style={styles.welcomeText}
          >
            Bonjour, <strong>{beneficiaryName}</strong>
          </motion.div>

          <motion.div
            style={styles.balanceCard}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.4, type: 'spring', stiffness: 100 }}
          >
            <div style={styles.balanceLabel}>Solde disponible</div>
            <div style={styles.balanceAmount}>
              {showBalance ? fmtMoney(account.solde, devise) : '••••••••'}
            </div>
            <div style={styles.balanceSubtext}>
              Compte courant · {account.iban ? `${account.iban.slice(0, 4)} •••• •••• ${account.iban.slice(-4)}` : 'IBAN non disponible'}
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Actions rapides */}
      <motion.div 
        style={styles.quickActions}
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.5 }}
      >
        <Link to="/banque/virement" style={styles.actionLink}>
          <motion.button 
            style={styles.actionButton}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <div style={styles.actionIcon}>
              <ArrowDownToLine size={24} />
            </div>
            <span style={styles.actionText}>Virement</span>
          </motion.button>
        </Link>

        <Link to="/banque/carte" style={styles.actionLink}>
          <motion.button 
            style={styles.actionButton}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <div style={styles.actionIcon}>
              <CreditCard size={24} />
            </div>
            <span style={styles.actionText}>Ma carte</span>
          </motion.button>
        </Link>

        <Link to="/banque/compte" style={styles.actionLink}>
          <motion.button 
            style={styles.actionButton}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <div style={styles.actionIcon}>
              <TrendingUp size={24} />
            </div>
            <span style={styles.actionText}>Détails</span>
          </motion.button>
        </Link>
      </motion.div>

      {/* Section Transactions */}
      <motion.div 
        style={styles.transactionsSection}
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.6 }}
      >
        <div style={styles.sectionHeader}>
          <h3 style={styles.sectionTitle}>Dernières opérations</h3>
          <button style={styles.refreshButton} onClick={() => window.location.reload()}>
            <RefreshCw size={16} />
          </button>
        </div>

        {loadingTx ? (
          <div style={styles.loadingState}>
            <div style={styles.spinner}></div>
            <p style={styles.loadingText}>Chargement des transactions...</p>
          </div>
        ) : txItems.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>💳</div>
            <p style={styles.emptyTitle}>Aucune transaction</p>
            <p style={styles.emptySubtext}>Vos opérations apparaîtront ici</p>
          </div>
        ) : (
          <div style={styles.txList}>
            {txItems.map((row, index) => {
              const statusInfo = getStatusInfo(row.status);
              const StatusIcon = statusInfo.icon;
              
              return (
                <motion.div
                  key={row.id}
                  style={styles.txItem}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 * index }}
                  whileHover={{ backgroundColor: '#F9FAFB', scale: 1.01 }}
                  onClick={() => setSelectedTx(row)}
                >
                  <div style={styles.txIcon}>
                    {row.incoming ? (
                      <div style={{...styles.iconCircle, backgroundColor: '#DCFCE7'}}>
                        <TrendingUp size={18} color="#16A34A" />
                      </div>
                    ) : (
                      <div style={{...styles.iconCircle, backgroundColor: '#FEE2E2'}}>
                        <ArrowDownToLine size={18} color="#DC2626" />
                      </div>
                    )}
                  </div>
                  
                  <div style={styles.txDetails}>
                    <div style={styles.txLabel}>{row.label}</div>
                    <div style={styles.txDateRow}>
                      <span style={styles.txDate}>
                        {row.date ? row.date.toLocaleDateString('fr-FR', { 
                          day: 'numeric', 
                          month: 'short', 
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : '—'}
                      </span>
                      {row.status && (
                        <div style={{...styles.statusBadge, backgroundColor: `${statusInfo.color}15`, color: statusInfo.color}}>
                          <StatusIcon size={12} />
                          <span>{statusInfo.label}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div style={styles.txRight}>
                    <div
                      style={{
                        ...styles.txAmount,
                        color: row.incoming ? '#16A34A' : '#DC2626',
                      }}
                    >
                      {row.incoming ? '+' : '−'}
                      {Number(row.amount).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })} {devise}
                    </div>
                    <ChevronRight size={16} color="#9CA3AF" />
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </motion.div>

      {/* Modal détails transaction */}
      <AnimatePresence>
        {selectedTx && (
          <motion.div
            style={styles.modalOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedTx(null)}
          >
            <motion.div
              style={styles.modalContent}
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 50, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={styles.modalHeader}>
                <h3 style={styles.modalTitle}>Détails de la transaction</h3>
                <button style={styles.closeButton} onClick={() => setSelectedTx(null)}>
                  <X size={24} />
                </button>
              </div>

              <div style={styles.modalBody}>
                {/* Montant principal */}
                <div style={styles.amountSection}>
                  <div style={{
                    ...styles.amountDisplay,
                    color: selectedTx.incoming ? '#16A34A' : '#DC2626'
                  }}>
                    {selectedTx.incoming ? '+' : '−'}{fmtMoney(selectedTx.amount, devise)}
                  </div>
                  {selectedTx.status && (() => {
                    const statusInfo = getStatusInfo(selectedTx.status);
                    const StatusIcon = statusInfo.icon;
                    return (
                      <div style={{
                        ...styles.statusBadgeLarge,
                        backgroundColor: `${statusInfo.color}15`,
                        color: statusInfo.color
                      }}>
                        <StatusIcon size={16} />
                        <span>{statusInfo.label}</span>
                      </div>
                    );
                  })()}
                </div>

                {/* Informations générales */}
                <div style={styles.detailSection}>
                  <div style={styles.detailRow}>
                    <span style={styles.detailLabel}>Type</span>
                    <span style={styles.detailValue}>{selectedTx.label}</span>
                  </div>
                  <div style={styles.detailRow}>
                    <span style={styles.detailLabel}>Date</span>
                    <span style={styles.detailValue}>
                      {selectedTx.date ? selectedTx.date.toLocaleDateString('fr-FR', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      }) : '—'}
                    </span>
                  </div>
                  <div style={styles.detailRow}>
                    <span style={styles.detailLabel}>Référence</span>
                    <span style={styles.detailValue}>#{selectedTx.id}</span>
                  </div>
                </div>

                {/* Détails du bénéficiaire (virements sortants) */}
                {!selectedTx.incoming && selectedTx.recipient && (
                  <div style={styles.recipientSection}>
                    <h4 style={styles.sectionSubtitle}>Bénéficiaire</h4>
                    <div style={styles.recipientCard}>
                      {selectedTx.recipient.bank_name && (
                        <div style={styles.detailRow}>
                          <span style={styles.detailLabel}>Banque</span>
                          <span style={styles.detailValue}>{selectedTx.recipient.bank_name}</span>
                        </div>
                      )}
                      {selectedTx.recipient.iban && (
                        <div style={styles.detailRow}>
                          <span style={styles.detailLabel}>IBAN</span>
                          <span style={{...styles.detailValue, fontFamily: 'monospace', fontSize: '13px'}}>
                            {selectedTx.recipient.iban}
                          </span>
                        </div>
                      )}
                      {selectedTx.recipient.swift && (
                        <div style={styles.detailRow}>
                          <span style={styles.detailLabel}>SWIFT/BIC</span>
                          <span style={{...styles.detailValue, fontFamily: 'monospace'}}>
                            {selectedTx.recipient.swift}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Message selon le statut */}
                {selectedTx.status === 'en_attente' && !selectedTx.incoming && (
                  <div style={styles.infoBox}>
                    <Clock size={18} color="#F59E0B" />
                    <div>
                      <div style={styles.infoTitle}>En attente de validation</div>
                      <div style={styles.infoText}>
                        Votre virement a été débité et est en cours de traitement par votre gestionnaire.
                      </div>
                    </div>
                  </div>
                )}

                {selectedTx.status === 'completed' && (
                  <div style={{...styles.infoBox, backgroundColor: '#DCFCE7', borderColor: '#BBF7D0'}}>
                    <CheckCircle2 size={18} color="#16A34A" />
                    <div>
                      <div style={{...styles.infoTitle, color: '#065F46'}}>Transaction complétée</div>
                      <div style={{...styles.infoText, color: '#047857'}}>
                        Cette opération a été finalisée avec succès.
                      </div>
                    </div>
                  </div>
                )}

                {selectedTx.status === 'rejected' && (
                  <div style={{...styles.infoBox, backgroundColor: '#FEE2E2', borderColor: '#FECACA'}}>
                    <AlertCircle size={18} color="#DC2626" />
                    <div>
                      <div style={{...styles.infoTitle, color: '#991B1B'}}>Transaction rejetée</div>
                      <div style={{...styles.infoText, color: '#7F1D1D'}}>
                        Ce virement a été refusé par votre gestionnaire.
                      </div>
                    </div>
                  </div>
                )}

        

                
              </div>

              <div style={styles.modalFooter}>
                <button style={styles.closeModalButton} onClick={() => setSelectedTx(null)}>
                  Fermer
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <MenuClient />
      <div style={{ height: '100px' }}></div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#F3F4F6',
    paddingBottom: '80px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  heroSection: {
    background: 'linear-gradient(135deg, #0061A6 0%, #003D6B 100%)',
    color: 'white',
    padding: '24px 20px 32px',
    borderBottomLeftRadius: '24px',
    borderBottomRightRadius: '24px',
    boxShadow: '0 4px 20px rgba(0, 97, 166, 0.3)',
  },
  heroContent: {
    maxWidth: '480px',
    margin: '0 auto',
  },
  headerTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  bankName: {
    fontSize: '20px',
    fontWeight: '700',
    letterSpacing: '-0.5px',
  },
  eyeButton: {
    background: 'rgba(255, 255, 255, 0.15)',
    backdropFilter: 'blur(10px)',
    border: 'none',
    borderRadius: '12px',
    padding: '10px',
    color: 'white',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s ease',
  },
  welcomeText: {
    fontSize: '15px',
    opacity: 0.9,
    marginBottom: '20px',
    fontWeight: '400',
  },
  balanceCard: {
    background: 'rgba(255, 255, 255, 0.15)',
    backdropFilter: 'blur(20px)',
    borderRadius: '20px',
    padding: '24px',
    border: '1px solid rgba(255, 255, 255, 0.2)',
  },
  balanceLabel: {
    fontSize: '13px',
    opacity: 0.85,
    marginBottom: '8px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontWeight: '500',
  },
  balanceAmount: {
    fontSize: '42px',
    fontWeight: '700',
    letterSpacing: '-1px',
    marginBottom: '8px',
  },
  balanceSubtext: {
    fontSize: '12px',
    opacity: 0.75,
    fontWeight: '400',
  },
  quickActions: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '12px',
    padding: '20px',
    maxWidth: '480px',
    margin: '0 auto',
    marginTop: '-20px',
  },
  actionLink: {
    textDecoration: 'none',
  },
  actionButton: {
    width: '100%',
    background: 'white',
    border: 'none',
    borderRadius: '16px',
    padding: '20px 12px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    transition: 'all 0.3s ease',
  },
  actionIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #0061A6 0%, #003D6B 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
  },
  actionText: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#111827',
  },
  transactionsSection: {
    padding: '20px',
    maxWidth: '480px',
    margin: '0 auto',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#111827',
    margin: 0,
  },
  refreshButton: {
    background: 'white',
    border: '1px solid #E5E7EB',
    borderRadius: '10px',
    padding: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    color: '#6B7280',
  },
  txList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  txItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    background: 'white',
    borderRadius: '16px',
    padding: '16px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  txIcon: {
    flexShrink: 0,
  },
  iconCircle: {
    width: '44px',
    height: '44px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  txDetails: {
    flex: 1,
    minWidth: 0,
  },
  txLabel: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#111827',
    marginBottom: '4px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  txDateRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
  txDate: {
    fontSize: '12px',
    color: '#6B7280',
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '3px 8px',
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: '600',
  },
  txRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexShrink: 0,
  },
  txAmount: {
    fontSize: '15px',
    fontWeight: '700',
    textAlign: 'right',
  },
  loadingState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    background: 'white',
    borderRadius: '16px',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid #E5E7EB',
    borderTopColor: '#0061A6',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  loadingText: {
    marginTop: '16px',
    fontSize: '14px',
    color: '#6B7280',
    margin: '16px 0 0 0',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    background: 'white',
    borderRadius: '16px',
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: '16px',
    opacity: 0.7,
  },
  emptyTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#111827',
    marginBottom: '8px',
    margin: '0 0 8px 0',
  },
  emptySubtext: {
    fontSize: '14px',
    color: '#6B7280',
    margin: 0,
  },
  statusBadgeLarge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: '600',
    marginTop: '8px',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: '24px',
    maxWidth: '500px',
    width: '100%',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
  },
  modalHeader: {
    padding: '24px',
    borderBottom: '1px solid #E5E7EB',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  modalTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#111827',
    margin: 0,
  },
  closeButton: {
    background: '#F3F4F6',
    border: 'none',
    borderRadius: '10px',
    padding: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    color: '#6B7280',
    transition: 'background 0.2s',
  },
  modalBody: {
    padding: '24px',
  },
  amountSection: {
    textAlign: 'center',
    paddingBottom: '24px',
    borderBottom: '1px solid #E5E7EB',
    marginBottom: '24px',
  },
  amountDisplay: {
    fontSize: '36px',
    fontWeight: '700',
    letterSpacing: '-1px',
  },
  detailSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginBottom: '24px',
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '16px',
  },
  detailLabel: {
    fontSize: '14px',
    color: '#6B7280',
    fontWeight: '500',
  },
  detailValue: {
    fontSize: '14px',
    color: '#111827',
    fontWeight: '600',
    textAlign: 'right',
    maxWidth: '60%',
    wordBreak: 'break-word',
  },
  sectionSubtitle: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#111827',
    marginBottom: '12px',
    margin: '0 0 12px 0',
  },
  recipientSection: {
    marginBottom: '24px',
  },
  recipientCard: {
    background: '#F9FAFB',
    borderRadius: '12px',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  infoBox: {
    display: 'flex',
    gap: '12px',
    padding: '16px',
    borderRadius: '12px',
    backgroundColor: '#FEF3C7',
    border: '1px solid #FDE68A',
    marginBottom: '16px',
  },
  infoTitle: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#92400E',
    marginBottom: '4px',
  },
  infoText: {
    fontSize: '13px',
    color: '#78350F',
    lineHeight: '1.5',
  },
  refundSection: {
    marginTop: '24px',
    paddingTop: '24px',
    borderTop: '1px solid #E5E7EB',
  },
  refundTextarea: {
    width: '100%',
    padding: '12px',
    borderRadius: '12px',
    border: '1px solid #D1D5DB',
    fontSize: '14px',
    fontFamily: 'inherit',
    resize: 'vertical',
    marginBottom: '12px',
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box',
  },
  refundButton: {
    width: '100%',
    background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    padding: '14px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    transition: 'transform 0.2s',
  },
  buttonSpinner: {
    width: '16px',
    height: '16px',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    borderTopColor: 'white',
    borderRadius: '50%',
    animation: 'spin 0.6s linear infinite',
  },
  modalFooter: {
    padding: '20px 24px',
    borderTop: '1px solid #E5E7EB',
  },
  closeModalButton: {
    width: '100%',
    background: '#F3F4F6',
    color: '#374151',
    border: 'none',
    borderRadius: '12px',
    padding: '14px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background 0.2s',
  },
};

export default Portefeuille;